import { computed } from "vue";
import { currencyMap } from "@/utils/currencyConfig";

interface FormatPriceOptions {
	/** Group thousands using the active locale ("11 990 kr"). Off by default. */
	groupThousands?: boolean;
}

/** Formatters are costly to build; one per locale + decimals is enough. */
const numberFormats = new Map<string, Intl.NumberFormat>();

const getNumberFormat = (locale: string, fractionDigits: number) => {
	const key = `${locale}|${fractionDigits}`;
	const cached = numberFormats.get(key);
	if (cached) return cached;
	const format = new Intl.NumberFormat(locale, {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits
	});
	numberFormats.set(key, format);
	return format;
};

export function useFormatPrice(options: FormatPriceOptions = {}) {
	const { groupThousands = false } = options;
	const norceStore = useNorceStore();
	const { localeProperties } = useI18n();

	const currencySettings = computed(() => {
		const currencyCode = norceStore?.currentCurrency?.Code || "";
		return (
			currencyMap[currencyCode] || { symbol: currencyCode, position: "prefix" }
		);
	});

	const localeTag = computed(() => localeProperties.value?.iso || "sv-SE");

	const formatAmount = (amount: number | undefined) => {
		if (typeof amount !== "number" || !Number.isFinite(amount)) {
			if (import.meta.dev) {
				console.warn(`[useFormatPrice] non-finite amount: ${amount}`);
			}
			// Legacy callers keep the pre-grouping output byte-for-byte.
			return groupThousands ? "" : `${amount}`;
		}
		if (!groupThousands) {
			return `${amount}`;
		}
		const fractionDigits = Number.isInteger(amount) ? 0 : 2;
		return getNumberFormat(String(localeTag.value), fractionDigits).format(
			amount
		);
	};

	// Function to format the price correctly
	const formatPrice = (amount: number | undefined) => {
		const { symbol, position } = currencySettings.value;
		const formatted = formatAmount(amount);
		return position === "prefix"
			? `${symbol} ${formatted}`
			: `${formatted} ${symbol}`;
	};

	return {
		formatAmount,
		formatPrice
	};
}
